import { DATE_PIPE_DEFAULT_OPTIONS, isPlatformBrowser } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, inject, PLATFORM_ID, provideAppInitializer } from '@angular/core';
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  PreloadAllModules,
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading
} from '@angular/router';
import { AppDateFormat } from '@common/constants/date-time-format.constant';
import { TIMEZONE } from '@common/constants/timezone.constants';
import { AppConfig } from '@common/tokens/app-configs.constant';
import { LocalStorage } from '@common/tokens/local-storage.constant';
import { WINDOW } from '@common/tokens/window.constant';
import { AssetHistoryApi } from '@core/apis/asset-history-log.api';
import { AssetsAiTaggingApi } from '@core/apis/assets-ai-tagging.api';
import { AssetsApi } from '@core/apis/assets.api';
import { DetectedAdditionalAssetApi } from '@core/apis/detected-additional-asset.api';
import { MetadataApi } from '@core/apis/metadata.api';
import { NotificationSSEApi } from '@core/apis/notification-sse.api';
import { NotificationApi } from '@core/apis/notification.api';
import { RelatedAssetsApi } from '@core/apis/related-asset.api';
import { ReportApi } from '@core/apis/report.api';
import { RoleApi } from '@core/apis/role.api';
import { UserHistoryLogApi } from '@core/apis/user-history-log.api';
import { UserManagementApi } from '@core/apis/user-management.api';
import { UserPreferenceApi } from '@core/apis/user-preference.api';
import { UserProfileApi } from '@core/apis/user-profile.api';
import { authHeaderInterceptor } from '@core/interceptors/auth-header.interceptor';
import { contextHeaderInterceptor } from '@core/interceptors/context-header.interceptor';
import { httpErrorInterceptor } from '@core/interceptors/http-error.interceptor';
import { GoogleTagManagerService } from '@core/services/google-tag-manager.service';
import { CustomTitleStrategy } from '@core/services/title-strategy.service';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { msalConfigProviders } from './app/msal.config';

function localStorageFactory(platformId: Record<string, unknown>): Storage {
  return (isPlatformBrowser(platformId) ? localStorage : {}) as Storage;
}

function windowFactory(platformId: Record<string, unknown>): Window {
  return <Window>(isPlatformBrowser(platformId) ? window : {});
}

fetch('configs/app-config.json')
  .then<AppConfigModel>(it => it.json())
  .then(res => {
    if (res.production) {
      enableProdMode();
    }
    return res;
  })
  .then(initialConfig =>
    bootstrapApplication(AppComponent, {
      providers: [
        { provide: AppConfig, useValue: initialConfig },
        provideAnimations(),
        provideAnimationsAsync(),
        provideRouter(
          routes,
          withPreloading(PreloadAllModules),
          withComponentInputBinding(),
          withInMemoryScrolling({
            scrollPositionRestoration: 'enabled',
            anchorScrolling: 'enabled'
          })
        ),
        provideHttpClient(
          withInterceptorsFromDi(),
          withFetch(),
          withInterceptors([contextHeaderInterceptor, authHeaderInterceptor, httpErrorInterceptor])
        ),
        provideAppInitializer(() => inject(GoogleTagManagerService).addScript()),
        ...msalConfigProviders,
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', subscriptSizing: 'dynamic' } },
        { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
        {
          provide: LocalStorage,
          useFactory: localStorageFactory,
          deps: [PLATFORM_ID]
        },
        {
          provide: WINDOW,
          useFactory: windowFactory,
          deps: [PLATFORM_ID]
        },
        { provide: TitleStrategy, useClass: CustomTitleStrategy },
        { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: AppDateFormat, timezone: TIMEZONE } },
        AssetsAiTaggingApi,
        AssetHistoryApi,
        UserHistoryLogApi,
        DetectedAdditionalAssetApi,
        RelatedAssetsApi,
        UserManagementApi,
        MetadataApi,
        AssetsApi,
        UserProfileApi,
        RoleApi,
        UserPreferenceApi,
        NotificationApi,
        NotificationSSEApi,
        ReportApi
      ]
    }).catch(err => console.error(err))
  );
